import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { PortalSiteEntity } from './portal-site.entity';

// Per-tenant / per-portal visibility + required overrides for the storefront
// registration form's STANDARD fields (PD-14370). A sibling to
// registration_field: that entity adds bespoke supplemental questions; this one
// tunes the fixed, built-in standard fields (company name, address block,
// business type, lead source, notes) so a tenant collects only the beyond-basic
// info it actually wants.
//
// A row is only written when a scope OVERRIDES the code default set
// (STANDARD_REGISTRATION_FIELDS); absent a row, the code default applies — so
// the defaults ship in code and every instance gets them with nothing to seed.
//
// Scope: `fk_portal_site__id` NULL = the tenant-wide override; a non-null portal
// id is a per-storefront override. Resolution is a PER-FIELD merge (code default
// -> tenant-default row -> portal row), see resolveStandardFieldConfig, so a
// portal can override a single field without re-declaring the rest. Uniqueness
// of (scope, fieldKey) is enforced in the service layer, NOT a DB unique index,
// because MySQL treats NULL `fk_portal_site__id` values as distinct.
@Index(
	'idx_registration_standard_field_portal',
	['fkPortalSiteId', 'fieldKey'],
	{},
)
@Entity('registration_standard_field')
export class RegistrationStandardFieldEntity extends BaseEntity {
	// Null = tenant-wide override; non-null = override for one portal_site.
	@Column('int', { name: 'fk_portal_site__id', nullable: true, unsigned: true })
	public fkPortalSiteId!: number | null;

	// One of STANDARD_REGISTRATION_FIELD_KEYS. Matches the storefront form key.
	@Column('varchar', { name: 'fieldKey', length: 60 })
	public fieldKey!: string;

	// Whether the field is shown on the storefront form for this scope.
	@Column('tinyint', { name: 'isVisible', width: 1, default: () => "'1'" })
	public isVisible!: boolean;

	// Whether the field is required (only enforced when also visible).
	@Column('tinyint', { name: 'isRequired', width: 1, default: () => "'0'" })
	public isRequired!: boolean;

	// Logical relation only (no DB-level FK constraint) so the additive
	// schema-sync just creates the column + index without managing a constraint.
	// Null for tenant-default rows.
	@ManyToOne(() => PortalSiteEntity, {
		nullable: true,
		createForeignKeyConstraints: false,
	})
	@JoinColumn([{ name: 'fk_portal_site__id', referencedColumnName: 'id' }])
	public fkPortalSite!: PortalSiteEntity | null;
}
